import * as React from 'react';
import { ClipboardPaste, X } from 'lucide-react';
import { OTPInput, REGEXP_ONLY_DIGITS } from 'input-otp';
import { cn } from '../lib/utils';
import { Button } from './button';

type ClipboardTextGetter = () => Promise<string> | string;

export type InputOTPProps = Omit<
  React.ComponentPropsWithoutRef<typeof OTPInput>,
  'value' | 'onChange' | 'maxLength' | 'render' | 'children' | 'onComplete'
> & {
  value: string;
  onChange: (value: string) => void;
  onComplete?: (value: string) => void;
  maxLength?: number;
  containerClassName?: string;
  ariaLabel?: string;
  showPasteButton?: boolean;
  pasteButtonLabel?: string;
  pasteButtonIcon?: React.ReactNode;
  showClearButtonWhenFilled?: boolean;
  clearButtonLabel?: string;
  clearButtonIcon?: React.ReactNode;
  getClipboardText?: ClipboardTextGetter;
};

const DEFAULT_MAX_LENGTH = 6;

function sanitizeInputOtpValue(value: string, maxLength: number): string {
  return value.replace(/\D/g, '').slice(0, maxLength);
}

function hasClipboardReader(getClipboardText?: ClipboardTextGetter): boolean {
  if (getClipboardText) {
    return true;
  }

  return typeof navigator !== 'undefined' && typeof navigator.clipboard?.readText === 'function';
}

async function readClipboardText(getClipboardText?: ClipboardTextGetter): Promise<string | null> {
  if (getClipboardText) {
    const clipboardText = await getClipboardText();
    return typeof clipboardText === 'string' ? clipboardText : null;
  }

  if (typeof navigator !== 'undefined' && typeof navigator.clipboard?.readText === 'function') {
    return navigator.clipboard.readText();
  }

  return null;
}

export const InputOTP = React.forwardRef<React.ElementRef<typeof OTPInput>, InputOTPProps>(
  (
    {
      value,
      onChange,
      onComplete,
      maxLength = 6,
      containerClassName,
      className,
      disabled,
      autoComplete = 'one-time-code',
      ariaLabel,
      showPasteButton = true,
      pasteButtonLabel,
      pasteButtonIcon,
      showClearButtonWhenFilled = false,
      clearButtonLabel,
      clearButtonIcon,
      getClipboardText,
      ...props
    },
    ref,
  ) => {
    const resolvedMaxLength = Math.max(1, maxLength || DEFAULT_MAX_LENGTH);
    const sanitizedValue = sanitizeInputOtpValue(value, resolvedMaxLength);
    const isOtpFilled = sanitizedValue.length === resolvedMaxLength;
    const canInteractWithAction = showPasteButton && !disabled && !props.readOnly;
    const shouldShowClearButton = isOtpFilled && showClearButtonWhenFilled && canInteractWithAction;
    const canShowPasteButton =
      canInteractWithAction && hasClipboardReader(getClipboardText);
    const shouldShowPasteButton = !shouldShowClearButton && canShowPasteButton;
    const shouldShowActionButton = shouldShowPasteButton || shouldShowClearButton;
    const pasteAriaLabel = pasteButtonLabel ?? ariaLabel;
    const clearAriaLabel = clearButtonLabel ?? ariaLabel ?? pasteButtonLabel;

    const applyNextValue = React.useCallback(
      (nextValueRaw: string) => {
        const nextValue = sanitizeInputOtpValue(nextValueRaw, resolvedMaxLength);
        onChange(nextValue);
        if (nextValue.length === resolvedMaxLength) {
          onComplete?.(nextValue);
        }
      },
      [onChange, onComplete, resolvedMaxLength],
    );

    const handlePaste = React.useCallback(async () => {
      const clipboardText = await readClipboardText(getClipboardText);
      if (clipboardText === null) {
        return;
      }
      applyNextValue(clipboardText);
    }, [applyNextValue, getClipboardText]);

    return (
      <OTPInput
        {...props}
        ref={ref}
        value={sanitizedValue}
        onChange={applyNextValue}
        maxLength={resolvedMaxLength}
        autoComplete={autoComplete}
        inputMode="numeric"
        pattern={REGEXP_ONLY_DIGITS}
        disabled={disabled}
        aria-label={ariaLabel}
        containerClassName={cn('flex items-center justify-center gap-2', containerClassName)}
        className={cn('disabled:cursor-not-allowed', className)}
        render={({ slots }) => (
          <div className="flex items-center justify-center gap-2">
            <div className="flex items-center justify-center gap-2">
              {slots.map((slot, index) => {
                const hasValue = Boolean(slot.char);
                return (
                  <div
                    key={index}
                    className={cn(
                      'flex h-10 w-10 items-center justify-center rounded-md border border-input bg-background text-sm font-medium text-foreground transition-colors',
                      hasValue ? 'border-foreground/30' : null,
                      slot.isActive ? 'border-ring ring-2 ring-ring/25' : null,
                      disabled ? 'opacity-50' : null,
                    )}
                  >
                    {slot.char ?? ''}
                  </div>
                );
              })}
            </div>
            {shouldShowActionButton ? (
              <Button
                type="button"
                variant="secondary"
                size="icon"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  if (shouldShowClearButton) {
                    applyNextValue('');
                    return;
                  }
                  void handlePaste();
                }}
                className="relative z-10 shrink-0 pointer-events-auto"
                aria-label={shouldShowClearButton ? clearAriaLabel : pasteAriaLabel}
              >
                {shouldShowClearButton
                  ? (clearButtonIcon ?? <X size={16} />)
                  : (pasteButtonIcon ?? <ClipboardPaste size={16} />)}
              </Button>
            ) : null}
          </div>
        )}
      />
    );
  },
);

InputOTP.displayName = 'InputOTP';
